import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
// leaflet.css is served through Sprockets (see app/assets/stylesheets/leaflet.css)
// instead of imported here — this project's webpack PostCSS chain is pinned to
// a Tailwind-only config and can't process leaflet's plain CSS.

// Free, keyless map (Leaflet + OpenStreetMap tiles) plotting every surgeon
// with real coordinates. Pins carry a popup linking to the profile.
export default class extends Controller {
  static values = { surgeons: Array, iconUrl: String, iconRetinaUrl: String, shadowUrl: String }

  connect() {
    const points = this.surgeonsValue.filter((s) => s.lat && s.lng)
    if (points.length === 0) return

    if (this.hasIconUrlValue) {
      // Leaflet's default _getIconUrl prepends the image path it sniffs from
      // leaflet.css ("images/") to whatever iconUrl we pass, which turned our
      // digested Sprockets URLs into /images//assets/leaflet/... 404s and left
      // every pin invisible. Dropping the override makes it use the URLs as-is.
      delete L.Icon.Default.prototype._getIconUrl
      L.Icon.Default.mergeOptions({
        iconUrl: this.iconUrlValue,
        iconRetinaUrl: this.iconRetinaUrlValue,
        shadowUrl: this.shadowUrlValue
      })
    }

    // zoomAnimation: false — Leaflet's default zoom animates via a CSS
    // transition and finalizes _zoom on the transitionend event. In this
    // app that event never fires reliably (seen consistently in-browser,
    // not just headless), so fitBounds()/setZoom() would silently get
    // stuck mid-animation and the map would freeze at zoom 0 (whole
    // world) with markers positioned for a zoom level that was never
    // actually reached. Disabling the animation makes every zoom/pan
    // apply instantly via _resetView instead, sidestepping that entirely.
    this.map = L.map(this.element, { scrollWheelZoom: false, zoomAnimation: false })
    // The map starts inside a display:none tab (Grid/Map toggle), so Leaflet
    // measures a zero-size container. Expose the instance so the toggle's
    // click handler can call invalidateSize() once the tab is actually shown.
    this.element._leafletMap = this.map

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 18
    }).addTo(this.map)

    const markers = points.map((s) => {
      const marker = L.marker([s.lat, s.lng]).addTo(this.map)
      marker.bindPopup(
        `<strong>${this.escapeHtml(s.name)}</strong><br>${this.escapeHtml(s.location || "")}<br><a href="/surgeons/${s.slug}">View profile &rarr;</a>`
      )
      return marker
    })

    this.group = L.featureGroup(markers)
    this.map.fitBounds(this.group.getBounds().pad(0.2), { maxZoom: 10 })
    // Also expose a refresh hook so code outside this controller (the
    // Grid/Map toggle) can fix the view after the container goes from
    // display:none to visible — see refresh() below for why this is needed.
    this.element._leafletRefresh = () => this.refresh()
  }

  // The map initializes while its tab is still display:none (zero-size
  // container), so the very first fitBounds() above computes a bogus
  // zoom/center from that zero size. Simply calling invalidateSize() after
  // the tab becomes visible fixes the container's pixel dimensions but
  // keeps rendering that same bogus view — it does not recompute bounds.
  // Re-running fitBounds() here, once the container has its real size, is
  // what actually corrects the zoom/center and marker positions.
  refresh() {
    if (!this.map) return
    this.map.invalidateSize()
    if (this.group) {
      this.map.fitBounds(this.group.getBounds().pad(0.2), { maxZoom: 10 })
    }
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
